import React, { PureComponent } from "react"
import classNames from "classnames"
import { Mutation } from "react-apollo"

import {
  UPDATE_FOUNDER_ASSIST_MODE,
  MY_FOUNDER_ASSIST_QUERY,
} from "../../lib/founder-assist-queries"

import "./founder-workspace-chip.scss"

const WORKSPACES = [
  {
    mode: "company",
    label: "Company plan",
    sub: "Plans grounded in your company",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    ),
  },
  {
    mode: "individual",
    label: "Personal plan",
    sub: "Plans grounded in you as a founder",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
      </svg>
    ),
  },
]

// Each workspace is now stored as its own FounderAssist row, so we can only
// describe the active one from the data we have. For the inactive workspace
// we fall back to the workspace's static tagline (set in WORKSPACES below).
function describeActiveWorkspace(fa) {
  if (!fa) return "Not started yet"
  if (!fa.questionnaireSavedAt) {
    return "Not started yet — answer a few questions to begin"
  }
  const openCount = (fa.actions || []).filter((a) => a.status === "open")
    .length
  if (openCount > 0) {
    return `${openCount} open action${openCount === 1 ? "" : "s"}`
  }
  return "Plan ready"
}

class FounderWorkspaceChip extends PureComponent {
  constructor(props) {
    super(props)
    this.state = { open: false, toast: null }
    this._ref = React.createRef()
    this._toastTimer = null
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    if (this._toastTimer) clearTimeout(this._toastTimer)
  }

  handleClickOutside = (e) => {
    if (
      this.state.open &&
      this._ref.current &&
      !this._ref.current.contains(e.target)
    ) {
      this.setState({ open: false })
    }
  }

  toggle = () => {
    this.setState((p) => ({ open: !p.open }))
  }

  select = (mode, currentMode, updateMode) => {
    this.setState({ open: false })
    if (mode === currentMode) return
    const targetWs = WORKSPACES.find((w) => w.mode === mode) || WORKSPACES[0]
    updateMode({ variables: { mode } })
      .then(({ data }) => {
        const result = data && data.updateFounderAssistMode
        const isFreshWorkspace = !(result && result.questionnaireSavedAt)
        const verb = isFreshWorkspace ? "Started" : "Switched to"
        this.setState({ toast: `${verb} your ${targetWs.label}.` })
        if (this._toastTimer) clearTimeout(this._toastTimer)
        this._toastTimer = setTimeout(() => {
          this.setState({ toast: null })
        }, 3000)
      })
      .catch(() => {
        // Errors are caught and swallowed silently — refetch will re-show
        // the current mode if the mutation failed server-side.
      })
  }

  render() {
    const { fa } = this.props
    const currentMode = (fa && fa.mode) || "company"
    const active = WORKSPACES.find((w) => w.mode === currentMode) || WORKSPACES[0]
    const { open } = this.state

    return (
      <Mutation
        mutation={UPDATE_FOUNDER_ASSIST_MODE}
        refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
      >
        {(updateMode) => (
          <div
            className={classNames("founder-workspace-chip", {
              "founder-workspace-chip--open": open,
              [`founder-workspace-chip--${currentMode}`]: true,
            })}
            ref={this._ref}
          >
            <button
              type="button"
              className="founder-workspace-chip__trigger"
              onClick={this.toggle}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <span
                className="founder-workspace-chip__trigger-icon"
                aria-hidden
              >
                {active.icon}
              </span>
              <span className="founder-workspace-chip__trigger-label">
                {active.label}
              </span>
              <span
                className={classNames("founder-workspace-chip__caret", {
                  "founder-workspace-chip__caret--open": open,
                })}
                aria-hidden
              />
            </button>
            {open && (
              <div className="founder-workspace-chip__menu" role="menu">
                <div className="founder-workspace-chip__menu-eyebrow">
                  Workspaces
                </div>
                {WORKSPACES.map((w) => {
                  const isActive = w.mode === currentMode
                  return (
                    <button
                      key={w.mode}
                      type="button"
                      role="menuitem"
                      className={classNames(
                        "founder-workspace-chip__menu-item",
                        {
                          "founder-workspace-chip__menu-item--active":
                            isActive,
                        }
                      )}
                      onClick={() => this.select(w.mode, currentMode, updateMode)}
                    >
                      <span
                        className={classNames(
                          "founder-workspace-chip__menu-icon",
                          `founder-workspace-chip__menu-icon--${w.mode}`
                        )}
                        aria-hidden
                      >
                        {w.icon}
                      </span>
                      <span className="founder-workspace-chip__menu-text">
                        <strong>{w.label}</strong>
                        <span>
                          {isActive ? describeActiveWorkspace(fa) : w.sub}
                        </span>
                      </span>
                      {isActive && (
                        <span
                          className="founder-workspace-chip__menu-check"
                          aria-label="active"
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  )
                })}
                <div className="founder-workspace-chip__menu-hint">
                  Each workspace keeps its own answers. Switch any time —
                  nothing is lost.
                </div>
              </div>
            )}
            {this.state.toast && (
              <div
                className="founder-workspace-chip__toast"
                role="status"
              >
                {this.state.toast}
              </div>
            )}
          </div>
        )}
      </Mutation>
    )
  }
}

export default FounderWorkspaceChip
